import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, Smiley, MapPin, Calendar, LockSimple } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Types ─────────────────────────────────────────
interface Sa2Area { sa2_code: string; sa2_name: string; area_sqkm: number }

// ── Constants ─────────────────────────────────────────
const COLORS = ["#F59E0B","#38BDF8","#34D399","#FBBF24","#F472B6","#A78BFA","#FB923C","#06B6D4","#E11D48","#8B5CF6","#10B981","#6366F1","#14B8A6","#D946EF"];

const FY_OPTIONS: IDropdownOption[] = [
  { key: "2024-25", text: "2024-25" },
  { key: "2023-24", text: "2023-24" },
  { key: "2022-23", text: "2022-23" },
];

const CHANNEL_OPTIONS: IDropdownOption[] = [
  { key: "all", text: "All Channels" },
  { key: "Phone", text: "Phone" },
  { key: "Email", text: "Email" },
  { key: "Web", text: "Web" },
  { key: "In Person", text: "In Person" },
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function fmtPct(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toFixed(1) + "%";
}
function truncLabel(s: string, max = 18): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: "linear-gradient(135deg, #EC4899, #F9A8D4)", borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", ":hover": { transform: "translateY(-2px)" },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function CustomerExperience() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mainData, setMainData] = useState<any[]>([]);
  const [sa2Areas, setSa2Areas] = useState<Sa2Area[]>([]);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  const [fy, setFy] = useState("2024-25");
  const [sa2, setSa2] = useState("all");
  const [channel, setChannel] = useState("all");

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (sa2 !== "all") params.set("sa2", sa2);
      if (channel !== "all") params.set("channel", channel);
      const [mainRes, sa2Res, compRes, trendRes] = await Promise.all([
        accessService.get(`/customer/contacts?${params}`),
        accessService.get("/abs/sa2-areas"),
        accessService.get(`/customer/comparison?fy=${fy}&metric=total_contacts`),
        accessService.get(`/customer/trend?metric=satisfaction_score`),
      ]);
      setMainData(mainRes.data);
      setSa2Areas(sa2Res.data);
      setComparisonData(compRes.data);
      setTrendData(trendRes.data);
    } catch (err: any) {
      setError(err.message || "Failed to load customer experience data");
    } finally {
      setLoading(false);
    }
  }, [fy, sa2, channel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Dropdown options ──
  const sa2Options: IDropdownOption[] = useMemo(() => [
    { key: "all", text: "All Boroondara" },
    ...sa2Areas.map(a => ({ key: a.sa2_code, text: a.sa2_name })),
  ], [sa2Areas]);

  // ── KPIs ──
  const kpis = useMemo(() => {
    if (mainData.length === 0) return null;
    const totalContacts = mainData.reduce((s: number, r: any) => s + (Number(r.total_contacts) || 0), 0);
    const satScores = mainData.filter((r: any) => r.satisfaction_score != null);
    const avgSatisfaction = satScores.length > 0
      ? satScores.reduce((s: number, r: any) => s + (Number(r.satisfaction_score) || 0), 0) / satScores.length
      : 0;
    const fcrRows = mainData.filter((r: any) => r.first_contact_resolution_pct != null);
    const avgFcr = fcrRows.length > 0
      ? fcrRows.reduce((s: number, r: any) => s + (Number(r.first_contact_resolution_pct) || 0), 0) / fcrRows.length
      : 0;
    const waitRows = mainData.filter((r: any) => r.avg_wait_minutes != null);
    const avgWait = waitRows.length > 0
      ? waitRows.reduce((s: number, r: any) => s + (Number(r.avg_wait_minutes) || 0), 0) / waitRows.length
      : 0;
    return { totalContacts, avgSatisfaction, avgFcr, avgWait };
  }, [mainData]);

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "People", color: "#EC4899", label: "Total Contacts", value: fmtNum(kpis.totalContacts), sub: `FY ${fy}` },
      { icon: "Emoji2", color: "#34D399", label: "Avg Satisfaction", value: fmtNum(kpis.avgSatisfaction.toFixed(1)) + "/5", sub: "across all channels" },
      { icon: "Completed", color: "#38BDF8", label: "First Contact Resolution", value: fmtPct(kpis.avgFcr), sub: "avg across areas" },
      { icon: "Clock", color: "#F59E0B", label: "Avg Wait Time", value: fmtNum(Math.round(kpis.avgWait)) + " min", sub: "avg across channels" },
    ];
  }, [kpis, fy]);

  // ── Chart data ──
  const contactsByChannel = useMemo(() => {
    const byChan: Record<string, number> = {};
    mainData.forEach((r: any) => {
      const ch = r.channel || "Unknown";
      byChan[ch] = (byChan[ch] || 0) + (Number(r.total_contacts) || 0);
    });
    return Object.entries(byChan).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  }, [mainData]);

  const satisfactionTrend = useMemo(() => {
    return trendData.map((r: any) => ({ financial_year: r.financial_year, value: Number(r.value || r.satisfaction_score) || 0 }));
  }, [trendData]);

  const contactsBySa2 = useMemo(() => {
    return comparisonData
      .map((r: any) => ({ name: r.label || r.sa2_name, value: Number(r.value || r.total_contacts) || 0 }))
      .sort((a: any, b: any) => b.value - a.value);
  }, [comparisonData]);

  const fcrByChannel = useMemo(() => {
    const byChan: Record<string, { total: number; count: number }> = {};
    mainData.forEach((r: any) => {
      const ch = r.channel || "Unknown";
      if (!byChan[ch]) byChan[ch] = { total: 0, count: 0 };
      if (r.first_contact_resolution_pct != null) {
        byChan[ch].total += Number(r.first_contact_resolution_pct) || 0;
        byChan[ch].count += 1;
      }
    });
    return Object.entries(byChan)
      .map(([name, { total, count }]) => ({ name, value: count > 0 ? total / count : 0 }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [mainData]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && mainData.length === 0) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading customer experience data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: '#EC4899', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Customer Experience</div>
          <div className={styles.subtitle}>
            Customer contacts, satisfaction scores, SLA compliance and wait times across {sa2Areas.length} SA2 areas.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><Smiley size={12} weight="regular" /> Customer Experience</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> {sa2Areas.length} SA2 Areas</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> FY 2022-23 to 2024-25</span>
            <span className={styles.chip}><LockSimple size={12} weight="regular" /> OFFICIAL: Sensitive</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Financial Year</div>
            <Dropdown
              selectedKey={fy}
              options={FY_OPTIONS}
              onChange={(_, opt) => opt && setFy(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>SA2 Area</div>
            <Dropdown
              selectedKey={sa2}
              options={sa2Options}
              onChange={(_, opt) => opt && setSa2(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Channel</div>
            <Dropdown
              selectedKey={channel}
              options={CHANNEL_OPTIONS}
              onChange={(_, opt) => opt && setChannel(String(opt.key))}
              styles={{ root: { width: 180 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartsRow}>
          {/* Pie: Contacts by Channel */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Contacts by Channel ({fy})</div>
            {contactsByChannel.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={contactsByChannel} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {contactsByChannel.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No channel data available
              </div>
            )}
          </div>

          {/* Line: Satisfaction Trend */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Satisfaction Score Trend</div>
            {satisfactionTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={satisfactionTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="financial_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 5]} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => Number(v).toFixed(2)} />
                  <Line type="monotone" dataKey="value" name="Satisfaction" stroke="#EC4899" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartsRow}>
          {/* Bar: Contacts by SA2 */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Contacts by SA2 ({fy})</div>
            {contactsBySa2.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={contactsBySa2} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Bar dataKey="value" name="Total Contacts" fill="#EC4899" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No contacts data available
              </div>
            )}
          </div>

          {/* Bar: FCR by Channel */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>First Contact Resolution by Channel ({fy})</div>
            {fcrByChannel.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={fcrByChannel} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => v + "%"} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtPct(v)} />
                  <Bar dataKey="value" name="FCR %" fill="#38BDF8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No FCR data available
              </div>
            )}
          </div>
        </div>

        {/* ── Data Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>Customer Data — FY {fy}</div>
          {mainData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Suburb</th>
                  <th className={styles.th}>Channel</th>
                  <th className={styles.th}>Contacts</th>
                  <th className={styles.th}>Wait (min)</th>
                  <th className={styles.th}>Satisfaction</th>
                  <th className={styles.th}>FCR%</th>
                  <th className={styles.th}>FY</th>
                  <th className={styles.th}>Quarter</th>
                </tr>
              </thead>
              <tbody>
                {mainData.map((row: any, i: number) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td}>{row.sa2_name}</td>
                    <td className={styles.td}>{row.channel}</td>
                    <td className={styles.td}>{fmtNum(row.total_contacts)}</td>
                    <td className={styles.td}>{fmtNum(row.avg_wait_minutes)}</td>
                    <td className={styles.td}>{row.satisfaction_score != null ? Number(row.satisfaction_score).toFixed(1) : "-"}</td>
                    <td className={styles.td}>{fmtPct(row.first_contact_resolution_pct)}</td>
                    <td className={styles.td}>{row.financial_year}</td>
                    <td className={styles.td}>{row.quarter}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No customer data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the Customer Experience Agent</div>
            <div className={styles.agentCtaSub}>
              Get instant answers about customer contacts, satisfaction scores, wait times and resolution rates across Boroondara.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}
